import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "defaultValue", "onChange", "value"];
import PropTypes from 'prop-types';
import React, { Children, cloneElement, forwardRef, useCallback, useState } from 'react';
import { ButtonGroup } from '../ButtonGroup/ButtonGroup';

/**
 * @extends ButtonGroup
 * @since 8.0.0
 */
export var ToggleButtonGroup = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var children = _ref.children,
    defaultValue = _ref.defaultValue,
    onChange = _ref.onChange,
    value = _ref.value,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(defaultValue),
    _useState2 = _slicedToArray(_useState, 2),
    valueState = _useState2[0],
    setValueState = _useState2[1];
  var controlled = typeof value !== 'undefined';
  var pressedValue = controlled ? value : valueState;
  var onClickCallback = useCallback(function (e, pressed, onClick) {
    // Get the next expected value
    var nextValue = pressedValue;
    if (!pressed) {
      nextValue = e.currentTarget.value;
    }

    // Only update if values differ
    if (nextValue !== pressedValue) {
      setValueState(nextValue);
      if (onChange) {
        onChange(nextValue);
      }
    }

    // Handles consumer provided `onClick`
    if (onClick) {
      onClick(e, e.currentTarget.value === nextValue);
    }
  }, [pressedValue, onChange]);
  var cloneChild = useCallback(function (child) {
    if (!child) {
      return null;
    }
    return /*#__PURE__*/cloneElement(child, {
      pressed: child.props.value === pressedValue,
      onClick: function onClick() {
        for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
          args[_key] = arguments[_key];
        }
        onClickCallback.apply(void 0, args.concat([child.props.onClick]));
      }
    });
  }, [onClickCallback, pressedValue]);
  return /*#__PURE__*/React.createElement(ButtonGroup, _extends({
    ref: forwardedRef
  }, rest), Children.map(children, cloneChild));
});
ToggleButtonGroup.displayName = 'ToggleButtonGroup';
ToggleButtonGroup.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * A group of `ToggleButton` components.
   */
  children: PropTypes.node.isRequired,
  /**
   * The `ButtonGroup` `conjoined`
   * prop, set to `true` by default for toggle button groups.
   */
  conjoined: PropTypes.bool,
  /**
   * Set the toggle button group value, and use as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   *
   * A toggle button group can set `value` or `defaultValue`, but not both.
   */
  defaultValue: PropTypes.string,
  /**
   * Callback for change events within the toggle button group. The callback will receive the
   * `value` of the currently selected `ToggleButton`, or `undefined` if all
   * buttons are unpressed.
   */
  onChange: PropTypes.func,
  /**
   * Set the toggle button group value, and use as an
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   *
   * A toggle button group can set `value` or `defaultValue`, but not both.
   */
  value: PropTypes.string
} : {};
ToggleButtonGroup.defaultProps = {
  conjoined: true,
  defaultValue: undefined,
  onChange: undefined,
  value: undefined
};